import React, { useState, useEffect, useMemo, useCallback } from 'react'
import styled from 'styled-components/macro'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Button from '@mui/material/Button'
import TableCell from '@mui/material/TableCell'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Grid from '@mui/material/Grid'
import Tabs from '@mui/material/Tabs'
import MuiTab from '@mui/material/Tab'

import PlusIcon from '@mui/icons-material/Add'

import DataListLayout from 'layouts/TabbedDataList'

import useDataList from 'hooks/useDataList'
import useDataListContext from 'hooks/context/useDataListContext'

import DataListTable from 'components/common/DataList/Table'
import AgencyPreferencesRow from 'components/common/DataList/Item/AgencyPreferences/Dayforce'
import useAgencyPreferences from 'hooks/agency_preferences/useAgencyPreferences'
import DayforceEditAgencyModal from 'components/agency_preferences/Dayforce/EditAgencyModal'
import {
  DATALIST_DIMENSIONS,
  DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPES,
  DAYFORCE_AGENCY_PREFERENCES_JURISDICTION_TYPES,
} from 'utils/constants'
import AsyncTextField from 'components/common/AsyncTextField'
import useModal from 'hooks/context/useModal'
import useNotification from 'hooks/context/useNotification'
import DayforceImportRecordsModal from 'components/agency_preferences/Dayforce/ImportRecordsModal'

const Tab = styled(MuiTab)`
  min-width: 160px;
`

function DayforceAgencyPreferencesTabs({ tabIndex, setTabIndex }) {
  return (
    <Box sx={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
      <Box
        sx={{
          bgcolor: 'white',
          height: '72px',
          position: 'absolute',
          overflow: 'hidden',
          width: '100%',
        }}
      />
      <Grid
        container
        sx={{
          height: '72px',
          width: '100%',
          minWidth: '1024px',
        }}
      >
        <Grid item md={6} lg={5}>
          <Tabs
            value={tabIndex}
            onChange={(_e, value) => setTabIndex(value)}
            aria-label="document approval tabs"
            sx={{ ml: 8, mt: 6, minWidth: '420px' }}
          >
            <Tab fullWidth label={`ACTIVE`} />
            <Tab fullWidth label={`INACTIVE`} />
          </Tabs>
        </Grid>
      </Grid>
      <Box
        fullWidth
        sx={{
          height: '1px',
          bgcolor: 'darkblue.main',
          opacity: 0.12,
        }}
      />
    </Box>
  )
}

function DayforceAgencyPreferecesPage() {
  const { setTotal, setTotalPages, page, setPage, pageSize } =
    useDataListContext()
  const { setOpen: setModalOpen, setModalProps } = useModal()
  const { setBasicNotification, setError } = useNotification()

  const [tabIndex, setTabIndex] = useState(0)
  const [filters, setFilters] = useState({})
  const [sortList, setSortList] = useState([])

  const hookParams = useMemo(() => {
    let params = {
      'paging[page]': page,
      'paging[page_size]': pageSize,
      ...sortList,
    }

    const searchTerms = []
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        searchTerms.push(`${key}|${value}`)
      }
    })

    if (tabIndex === 1) {
      searchTerms.push('is_active|0')
    } else {
      searchTerms.push('is_active|1')
    }

    if (searchTerms.length > 0) {
      params.search = searchTerms.join('|')
    }

    return params
  }, [filters, page, pageSize, sortList, tabIndex])

  const {
    agencyPreferences,
    total,
    totalPages,
    loading,
    addAgency,
    downloadCsv,
    updateAgencyPreferences,
    uploadCsv,
    executeCsvImport,
  } = useAgencyPreferences({
    params: hookParams,
    clientPaginated: false,
  })

  useEffect(() => {
    if (total !== undefined) {
      setTotal(total)
      setTotalPages(totalPages)
    }
  }, [total])

  const {
    dataList,
    columns,
    dataListProps,
    loading: dataLoading,
  } = useDataList({
    baseRowData: agencyPreferences,
    presetName: 'agencyMailing',
    setSortList,
  })

  const [activeAgency, setActiveAgency] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showImportRecordsModal, setShowImportRecordsModal] = useState(false)

  const openAgencyModal = useCallback(
    (agency, action = 'edit') => {
      if (action === 'edit') {
        setActiveAgency(agency)
        setShowEditModal(true)
      }

      if (action === 'activate' || action === 'deactivate') {
        const modalProps = {
          title: `Deactivate Tax Code ${agency?.tax_code}`,
          children: 'Are you sure you want to deactivate this tax code?',
          subTitle: undefined,
          scrollable: false,
          width: '414px',
          height: '324px',
          hideButtonsBorder: true,
          footerButtonProps: [
            {
              children: 'Deactivate',
              color: 'primary',
              variant: 'contained',
              size: 'action-header',
              onClick: async () => {
                try {
                  await updateAgencyPreferences({
                    agencyId: agency.id,
                    values: {
                      is_active: action === 'deactivate' ? 0 : 1,
                    },
                  })
                  setModalOpen(false)
                  setBasicNotification(
                    <>
                      Tax code has been successfully{' '}
                      {action === 'deactivate' ? 'deactivated' : 'activated'}
                    </>
                  )
                } catch (err) {
                  setModalOpen(false)
                  setError(err?.response?.data?.display_message || err?.message)
                }
              },
            },
            {
              children: 'Cancel',
              color: 'primary',
              variant: 'outlined',
              onClick: () => setModalOpen(false),
              size: 'action-header',
            },
          ],
        }

        if (action === 'activate') {
          modalProps.title = `Activate Tax Code ${agency?.tax_code}`
          modalProps.children =
            'Are you sure you want to activate this tax code?'
          modalProps.footerButtonProps[0].children = 'Activate'
        }

        setModalProps(modalProps)
        setModalOpen(true)
      }
    },
    [updateAgencyPreferences]
  )

  const handleAddAgency = useCallback(
    async ({ values }) => {
      const res = await addAgency({ values })
      setActiveAgency(res.data)
    },
    [addAgency]
  )

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target
    setFilters((prev) => {
      const newFilters = { ...prev }
      if (value) {
        newFilters[name] = value
      } else {
        delete newFilters[name]
      }
      return newFilters
    })
  }, [])

  const renderTable = useMemo(() => {
    return (
      <DataListTable
        {...dataListProps}
        height={DATALIST_DIMENSIONS.TABBED_HEIGHT}
        rowKey="tax_code"
        dataList={dataList}
        columns={columns}
        loading={loading || dataLoading}
        filters={filters}
        sortList={sortList}
        setSortList={setSortList}
        RowComponent={AgencyPreferencesRow}
        rowProps={{
          openAgencyModal: openAgencyModal,
        }}
        total={total}
        visibleColumns={[
          'tax_code',
          'document_type',
          'tax_code_description',
          'jurisdiction_type',
          'updated_by',
          'datetime_modified',
        ]}
        extraColumns={
          <>
            <TableCell sx={{ backgroundColor: 'white' }}></TableCell>
            <TableCell sx={{ backgroundColor: 'white' }}></TableCell>
          </>
        }
      />
    )
  }, [
    dataListProps,
    dataList,
    columns,
    loading,
    dataLoading,
    filters,
    sortList,
    openAgencyModal,
    total,
  ])

  useEffect(() => {
    if (page !== 1) {
      setPage(1)
    }
  }, [filters])

  return (
    <>
      <DayforceEditAgencyModal
        open={showEditModal}
        setOpen={setShowEditModal}
        agency={activeAgency}
        updateAgencyPreferences={updateAgencyPreferences}
        addAgency={handleAddAgency}
      />
      <DayforceImportRecordsModal
        open={showImportRecordsModal}
        setOpen={setShowImportRecordsModal}
        uploadCsv={uploadCsv}
        executeCsvImport={executeCsvImport}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <DayforceAgencyPreferencesTabs
          tabIndex={tabIndex}
          setTabIndex={setTabIndex}
        />
        <Box sx={{ px: 8, pt: 6, width: '100%' }}>
          <Box sx={{ display: 'flex', height: '40px', mb: 6 }}>
            <Box>
              <Button
                startIcon={<PlusIcon />}
                variant="contained"
                color="primary"
                sx={{ mr: 4, whiteSpace: 'nowrap', height: '40px' }}
                onClick={() => openAgencyModal(null)}
              >
                Add Preference
              </Button>
            </Box>
            <Grid container spacing={2} columns={12}>
              <Grid item xs={12} md={3}>
                <AsyncTextField
                  size="small"
                  label="Tax Code"
                  variant="outlined"
                  value={filters.tax_code || ''}
                  fullWidth
                  onChange={handleFilterChange}
                  name="tax_code"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  variant="outlined"
                  name="document_type"
                  label="Document Type"
                  value={filters.document_type || ''}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">Any</MenuItem>
                  {Object.keys(DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPES).map(
                    (type) => (
                      <MenuItem key={type} value={type}>
                        {DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPES[type]}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <AsyncTextField
                  size="small"
                  label="Tax Code Description"
                  variant="outlined"
                  value={filters.tax_code_description || ''}
                  fullWidth
                  onChange={handleFilterChange}
                  name="tax_code_description"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  variant="outlined"
                  name="jurisdiction_type"
                  label="Jurisdiction Type"
                  value={filters.jurisdiction_type || ''}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">Any</MenuItem>
                  {Object.entries(
                    DAYFORCE_AGENCY_PREFERENCES_JURISDICTION_TYPES
                  ).map(([value, display]) => (
                    <MenuItem key={value} value={value}>
                      {display}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Box>
              <Button
                variant="contained"
                color="primary"
                sx={{ mx: 2, whiteSpace: 'nowrap', height: '40px' }}
                onClick={() => {
                  if (!filters.document_type) {
                    setError(
                      'Please select a Document Type filter in order to Export records.'
                    )
                  } else {
                    downloadCsv()
                  }
                }}
              >
                Export Records
              </Button>
            </Box>
            <Box>
              <Button
                variant="contained"
                color="primary"
                sx={{ whiteSpace: 'nowrap', height: '40px' }}
                onClick={() => setShowImportRecordsModal(true)}
              >
                Import Records
              </Button>
            </Box>
          </Box>

          <Card sx={{ boxShadow: 2, width: '100%' }}>{renderTable}</Card>
        </Box>
      </Box>
    </>
  )
}

function DayforceAgencyPreferecesPageWrapper() {
  return (
    <DataListLayout>
      <DayforceAgencyPreferecesPage />
    </DataListLayout>
  )
}

export default DayforceAgencyPreferecesPageWrapper
